import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getJobDetails, uploadJobImages, deleteJobImage, updateJobDetails, formatTimestamp, formatFrequency, formatAllocationDetails, getGoogleMapsUrl, validateMonthlyHours, checkNearbyCompletedJobs, getEarlierIncompleteJobs, cancelJobs } from '../services/jobService';
import { getDocumentSignedUrl } from '../services/complianceService';
import { useConfig } from '../context/ConfigContext';
import { useAdminView } from '../context/AdminViewContext';
import { useSuspension } from '../context/SuspensionContext';
import StatusBadge from '../components/StatusBadge';
import CompletionModal from '../components/modals/CompletionModal';
import DuplicateWarningModal from '../components/modals/DuplicateWarningModal';
import HourUpdateModal from '../components/modals/HourUpdateModal';
import MonthlyAllocationModal from '../components/modals/MonthlyAllocationModal';
import EarlierJobsModal from '../components/modals/EarlierJobsModal';
import CancelJobModal from '../components/modals/CancelJobModal';
import { FaMapMarkerAlt, FaHourglassHalf, FaArrowLeft, FaUpload, FaTrash, FaExclamationTriangle, FaFilePdf, FaPhoneAlt } from 'react-icons/fa';
import { terminology } from '../config/terminology';
import { getLocalDateString, formatDateToInput } from '../utils/dateUtils';

const JobDetailsPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { statuses: configStatuses, loadingConfig, configError } = useConfig();
  const { isAdminView } = useAdminView();
  const { isSuspended } = useSuspension();
  // Same read-only treatment as admin-view impersonation: hide/disable every write affordance below.
  const isReadOnly = isAdminView || isSuspended;
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editableStatus, setEditableStatus] = useState('');
  const [originalStatus, setOriginalStatus] = useState('');
  const [actualHours, setActualHours] = useState('');
  const [originalActualHours, setOriginalActualHours] = useState('');
  const [isNoPhotoJob, setIsNoPhotoJob] = useState(false);
  const [originalIsNoPhotoJob, setOriginalIsNoPhotoJob] = useState('');
  const [priceQuotePerHour, setPriceQuotePerHour] = useState('');
  const [originalPriceQuotePerHour, setOriginalPriceQuotePerHour] = useState('');
  const [saveError, setSaveError] = useState('');
  // State for Notes for Admin
  const [notesForAdmin, setNotesForAdmin] = useState('');
  const [originalNotesForAdmin, setOriginalNotesForAdmin] = useState('');

  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  // Set when the completion response reports a failure, before the job doc is re-read.
  const [billingJustFailed, setBillingJustFailed] = useState(false);
  const [completionSuggestion, setCompletionSuggestion] = useState(''); // Reinstated
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionDate, setCompletionDate] = useState(getLocalDateString());
  const [editableJobDate, setEditableJobDate] = useState('');
  const [originalJobDate, setOriginalJobDate] = useState('');
  // Backs the "Completed on" field, which replaces "Scheduled for" once Completed is selected.
  const [editableCompletionDate, setEditableCompletionDate] = useState('');
  const [originalCompletionDate, setOriginalCompletionDate] = useState('');

  // State for the new hour update modal
  const [showHourUpdateModal, setShowHourUpdateModal] = useState(false);
  const [hourUpdateModalInfo, setHourUpdateModalInfo] = useState(null); // Added back

  // State for monthly allocation tracking
  const [monthlyAllocationInfo, setMonthlyAllocationInfo] = useState(null);
  const [showMonthlyAllocationModal, setShowMonthlyAllocationModal] = useState(false);
  const [monthlyAllocationModalInfo, setMonthlyAllocationModalInfo] = useState(null);

  // State for duplicate job warning modal
  const [showDuplicateWarningModal, setShowDuplicateWarningModal] = useState(false);
  const [duplicateJobInfo, setDuplicateJobInfo] = useState(null);

  // Confirmation shown before a job is cancelled, so a reschedule is not mistaken for a cancellation.
  const [showCancelJobModal, setShowCancelJobModal] = useState(false);

  // State for earlier incomplete jobs modal (when completing a job with earlier scheduled jobs)
  const [showEarlierJobsModal, setShowEarlierJobsModal] = useState(false);
  const [earlierIncompleteJobs, setEarlierIncompleteJobs] = useState([]);
  const [cancellingEarlierJobs, setCancellingEarlierJobs] = useState(false);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResultUrls, setUploadResultUrls] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const dateInputRef = useRef(null);

  const [deletingImage, setDeletingImage] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [deletedPhotoUrls, setDeletedPhotoUrls] = useState([]);

  const updateJobPhotosInBackend = async (newPhotoList) => {
    try {
      setSaving(true);
      setSaveError('');
      await updateJobDetails(jobId, { photoUrls: newPhotoList });
      setJob(prevJob => ({ ...prevJob, photoUrls: newPhotoList }));
      return true;
    } catch (err) {
      setSaveError(`Failed to save photo changes: ${err.message}`);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const fetchDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSaveError('');
    setSuccessMessage('');
    setCompletionSuggestion(''); // Reinstated
    setUploadResultUrls([]);
    setDeletedPhotoUrls([]);
    // Reset notesForAdmin as well
    setNotesForAdmin('');
    setOriginalNotesForAdmin('');
    try {
      const jobData = await getJobDetails(jobId);
      setJob(jobData);
      if (jobData.status) {
        setEditableStatus(jobData.status);
        setOriginalStatus(jobData.status);
        const initialHours = jobData.actualHours != null ? String(jobData.actualHours) : '';
        setActualHours(initialHours);
        setOriginalActualHours(initialHours);
        const initialIsNoPhoto = jobData.isNoPhotoJob || false;
        setIsNoPhotoJob(initialIsNoPhoto);
        setOriginalIsNoPhotoJob(initialIsNoPhoto);

        const initialQuoteValue = jobData.status === 'pending-quote'
          ? (jobData.priceQuotePerHour != null ? String(jobData.priceQuotePerHour) : '')
          : (jobData.pricePerHour != null ? String(jobData.pricePerHour) : '');
        setPriceQuotePerHour(jobData.status === 'pending-quote' ? initialQuoteValue : '');
        setOriginalPriceQuotePerHour(jobData.status === 'pending-quote' ? initialQuoteValue : '');

        const initialDate = formatDateToInput(jobData.jobDate);
        setEditableJobDate(initialDate);
        setOriginalJobDate(initialDate);

        const initialCompletionDate = formatDateToInput(jobData.completionDate);
        setEditableCompletionDate(initialCompletionDate);
        setOriginalCompletionDate(initialCompletionDate);

        // Initialize Notes for Admin
        setNotesForAdmin(jobData.notesForAdmin || '');
        setOriginalNotesForAdmin(jobData.notesForAdmin || '');
      }
    } catch (err) {
      setError(err.message || 'Could not load job details.');
      setJob(null);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  const fetchMonthlyAllocationInfo = useCallback(async (jobData) => {
    if (jobData?.allocationType === 'monthly' && jobData?.id) {
      try {
        console.log('[fetchMonthlyAllocationInfo] Fetching monthly allocation info for job:', jobData.id);
        const validationResult = await validateMonthlyHours(jobData.id, 0); // Use 0 to just get current status
        if (validationResult.status === 'success' && validationResult.isMonthlyAllocation) {
          setMonthlyAllocationInfo(validationResult);
          console.log('[fetchMonthlyAllocationInfo] Monthly allocation info loaded:', validationResult);
        }
      } catch (error) {
        console.error('[fetchMonthlyAllocationInfo] Error fetching monthly allocation info:', error);
      }
    } else {
      setMonthlyAllocationInfo(null);
    }
  }, []);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  useEffect(() => {
    if (job) {
      fetchMonthlyAllocationInfo(job);
    }
  }, [job, fetchMonthlyAllocationInfo]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    setSaveError('');

    if (originalStatus === 'pending-quote') {
        console.warn("Status cannot be changed from 'pending-quote' via dropdown.");
        e.target.value = editableStatus;
        return;
    }

    // Status (including 'completed') can be selected freely here. The required-field
    // validation for completion (actual hours + photos) runs on Save — see handleSave.
    setEditableStatus(newStatus);
    // The date field turns into "Completed on" as soon as this is picked, so give it a value.
    if (newStatus === 'completed' && !editableCompletionDate) {
      setEditableCompletionDate(getLocalDateString());
    }
    console.log("Status changed to:", newStatus);
  };

  const handleActualHoursChange = (e) => {
    if (originalStatus === 'pending-quote') return;
    setActualHours(e.target.value);
    setSaveError('');
  };

  // const handleReasonForDelayChange = (e) => { // Removed
  //   setReasonForDelay(e.target.value); // Removed
  //   setSaveError(''); // Removed
  // }; // Removed

  // Handler for Notes for Admin
  const handleNotesForAdminChange = (e) => {
    setNotesForAdmin(e.target.value);
    setSaveError('');
  };

  const handlePriceQuoteChange = (e) => {
    setPriceQuotePerHour(e.target.value);
    setSaveError('');
  };

  const handleNoPhotoChange = (e) => {
    if (originalStatus === 'pending-quote') return;
    const checked = e.target.checked;
    setIsNoPhotoJob(checked);
    setSaveError('');
    if (checked) {
      setUploadError(null);
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleJobDateChange = (e) => {
    const newDateStr = e.target.value;
    setSaveError(''); // Clear previous errors

    // Basic validation: Check if a date is selected
    if (!newDateStr) {
      setSaveError("Job Date cannot be empty.");
      setEditableJobDate(newDateStr); // Allow clearing but show error
      setCompletionDate(newDateStr)
      return;
    }

    // Past date validation
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of today for comparison
    const selectedDate = new Date(newDateStr);
    const selectedDateLocal = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());

    // if (selectedDateLocal < today) {
    //   setSaveError("Job Date cannot be in the past.");
    // }

    setEditableJobDate(newDateStr);
  };

  const handleCompletionDateChange = (e) => {
    setSaveError('');
    setEditableCompletionDate(e.target.value);
  };

  const processFiles = (files) => {
    if (isNoPhotoJob) {
      setUploadError("Cannot upload files when 'No photos required' is checked.");
      return;
    }

    const currentTotalPhotos = (job?.photoUrls?.length || 0) + uploadResultUrls.length;
    const allowedNewFiles = 5 - currentTotalPhotos;

    if (allowedNewFiles <= 0) {
      setUploadError("Maximum of 5 files already uploaded.");
      return;
    }

    if (files.length > allowedNewFiles) {
      setUploadError(`You can only upload ${allowedNewFiles} more file(s). Please select fewer files.`);
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const filesToSelect = files.slice(0, allowedNewFiles);
    setSelectedFiles(filesToSelect);
    setUploadError(null);
  };

  const handleFileChange = (event) => {
    processFiles(Array.from(event.target.files));
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!isNoPhotoJob && canUploadMore && !uploading && !saving && !deletingImage) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    if (isNoPhotoJob || !canUploadMore || uploading || saving || !!deletingImage) return;
    const files = Array.from(event.dataTransfer.files);
    processFiles(files);
  };

  const triggerFileInput = () => {
    if (isNoPhotoJob) {
      setUploadError("Cannot choose files when 'No photos required' is checked.");
      return;
    }
    setUploadError(null);
    fileInputRef.current?.click();
  };

  const handleUpload = async () => {
    if (isNoPhotoJob) {
      setUploadError("Cannot upload photos when 'No photos required' is checked.");
      return;
    }

    if (selectedFiles.length === 0) {
      setUploadError('Please select at least one image file.');
      return;
    }

    setUploading(true);
    setUploadError(null);
    setSaveError('');

    const formData = new FormData();
    selectedFiles.forEach((file) => {
      formData.append('jobImages', file);
    });

    try {
      const uploadResult = await uploadJobImages(formData);
      const newlyUploadedUrls = uploadResult.urls || [];

      if (newlyUploadedUrls.length > 0) {
        const currentPhotoUrls = job?.photoUrls || [];
        const newCompleteList = [...currentPhotoUrls, ...newlyUploadedUrls];

        const updateSuccess = await updateJobPhotosInBackend(newCompleteList);

        if (updateSuccess) {
          setUploadResultUrls([]);
          setSelectedFiles([]);
          setSuccessMessage(`${newlyUploadedUrls.length} image(s) uploaded and saved successfully!`);
        } else {
          setUploadResultUrls(prev => [...prev, ...newlyUploadedUrls]);
        }
      } else {
        setSuccessMessage("No images were uploaded.");
      }

    } catch (err) {
      setUploadError(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleViewPhoto = async (photoUrl) => {
    if (!photoUrl) return;
    try {
      const signedUrl = await getDocumentSignedUrl(photoUrl);
      window.open(signedUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Error opening photo:', err);
    }
  };

  const handleDeleteImage = async (imageUrlToDelete) => {
    if (deletingImage || saving) return;

    setDeletingImage(imageUrlToDelete);
    setDeleteError(null);
    setSaveError('');

    try {
      await deleteJobImage(imageUrlToDelete);

      const currentPhotoUrls = job?.photoUrls || [];
      const newCompleteList = currentPhotoUrls.filter(url => url !== imageUrlToDelete);

      const updateSuccess = await updateJobPhotosInBackend(newCompleteList);

      if (updateSuccess) {
        setUploadResultUrls(prevUrls => prevUrls.filter(url => url !== imageUrlToDelete));
        setDeletedPhotoUrls([]);
        setSuccessMessage('Image deleted and changes saved successfully.');
      } else {
        fetchDetails();
      }

    } catch (err) {
      setDeleteError(`Failed to delete image: ${err.message}`);
      fetchDetails();
    } finally {
      setDeletingImage(null);
    }
  };

  const handleSave = async (bypassHourModalCheck = false, bypassCancelModal = false) => {
    console.log('[handleSave] Called with bypassHourModalCheck:', bypassHourModalCheck);

    // Define variables needed for the log and subsequent logic upfront
    const isFixedFee = job?.allocationType === 'fixed-fee';
    const isPerVisit = job?.allocationType === 'per-visit';
    const statusChanged = editableStatus !== originalStatus;
    const isNoPhotoJobChanged = isNoPhotoJob !== originalIsNoPhotoJob;
    const rawActualHoursChanged = actualHours !== originalActualHours;
    const editableJobDateChanged = editableJobDate !== originalJobDate; // Renamed to avoid conflict, and ensure it's clear this is for the editable one
    const notesForAdminChanged = notesForAdmin !== originalNotesForAdmin;

    // Corrected isDirty logic (moved from later in the function)
    const isDirty = statusChanged ||
                  isNoPhotoJobChanged ||
                  (!isFixedFee && rawActualHoursChanged) ||
                  editableJobDateChanged || // Use the renamed variable
                  notesForAdminChanged;

    console.log('[handleSave] Current state: editableStatus:', editableStatus, 'originalStatus:', originalStatus, 'actualHours:', actualHours, 'originalActualHours:', originalActualHours, 'isFixedFee:', isFixedFee, 'isDirty:', isDirty);
    setSaveError('');
    setSuccessMessage('');
    setCompletionSuggestion(''); // Reinstated: Clear previous suggestion

    // A completed job exposes only "Completed on". Save it alone so the scheduled date,
    // status and hours are all left untouched.
    if (originalStatus === 'completed') {
      if (editableCompletionDate === originalCompletionDate || saving) {
        if (!saving) setSuccessMessage("No changes to save.");
        return;
      }
      if (!editableCompletionDate) {
        setSaveError("Completed on date cannot be empty.");
        return;
      }
      if (editableCompletionDate > getLocalDateString()) {
        setSaveError("Completed on date cannot be in the future.");
        return;
      }

      setSaving(true);
      try {
        await updateJobDetails(jobId, { completionDate: editableCompletionDate });
        setOriginalCompletionDate(editableCompletionDate);
        setJob(prevJob => ({ ...prevJob, completionDate: editableCompletionDate }));
        setSuccessMessage("Completion date updated successfully!");
      } catch (err) {
        setSaveError(`Failed to save changes: ${err.message}`);
      } finally {
        setSaving(false);
      }
      return;
    }

    if (originalStatus === 'pending-quote') {
      const quote = priceQuotePerHour.trim() === '' ? null : parseFloat(priceQuotePerHour);
      if (quote === null || isNaN(quote) || quote <= 0) {
        setSaveError("Please enter a valid Price Quote (must be a positive number).");
        return;
      }

      const quoteChanged = priceQuotePerHour !== originalPriceQuotePerHour;
      if (!quoteChanged || saving) {
        return;
      }

      const dataToUpdate = {
        priceQuotePerHour: quote,
        status: 'quote-received',
      };

      console.log("Submitting quote - Data being sent to updateJobDetails:", dataToUpdate);

      setSaving(true);
      try {
        await updateJobDetails(jobId, dataToUpdate);
        setSuccessMessage("Quote submitted successfully! Status updated to Quote Received.");

        setOriginalStatus('quote-received');
        setEditableStatus('quote-received');
        setOriginalPriceQuotePerHour(priceQuotePerHour);

        setJob(prevJob => ({
          ...prevJob,
          status: 'quote-received',
          priceQuotePerHour: quote,
        }));

      } catch (err) {
        console.error("Error submitting quote via API:", err);
        setSaveError(`Failed to submit quote: ${err.message}`);
      } finally {
        setSaving(false);
      }
      return;
    }

    // Cancelling is not the same as rescheduling, so confirm it and point the franchisee at the
    // reschedule option before the status is written.
    if (statusChanged && editableStatus === 'cancelled' && !bypassCancelModal) {
      setShowCancelJobModal(true);
      return;
    }

    // --- COMPLETION REQUIREMENTS VALIDATION (on save) ---
    // The status dropdown lets the user pick 'completed' freely; the required-field
    // checks happen here, at save time. If transitioning to completed, require valid
    // actual hours (unless fixed-fee) and at least one photo (unless "No photos required").
    if (editableStatus === 'completed' && originalStatus !== 'completed') {
      const hoursValue = isFixedFee ? job?.allotedHours : parseFloat(actualHours);
      const hasValidHoursForCompletion = isFixedFee || (hoursValue != null && !isNaN(hoursValue) && hoursValue > 0);
      const photosRequirementMet = isNoPhotoJob || (job?.photoUrls?.length || 0) > 0;

      if (!hasValidHoursForCompletion || !photosRequirementMet) {
        const errors = [];
        if (!hasValidHoursForCompletion) {
          errors.push('Actual Hours must be entered and greater than 0');
        }
        if (!photosRequirementMet) {
          errors.push('at least one photo must be uploaded (or "No photos required" checked)');
        }
        setSaveError('Cannot complete job: ' + errors.join(' and ') + '.');
        return;
      }
    }

    const jobDateChanged = editableJobDate !== originalJobDate; // This was the one that had a duplicate, ensure it's declared once.
    if (jobDateChanged) {
      if (!editableJobDate) {
        setSaveError("Job Date cannot be empty.");
        return;
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(editableJobDate);
      const selectedDateLocal = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());

      // if (selectedDateLocal < today) {
      //   setSaveError("Job Date cannot be in the past.");
      //   return;
      // }
    }

    // const isFixedFee = job?.allocationType === 'fixed-fee'; // MOVED UP
    // const isPerVisit = job?.allocationType === 'per-visit'; // MOVED UP

    // const rawActualHoursChanged = actualHours !== originalActualHours; // MOVED UP
    // actualHoursChanged depends on isFixedFee and rawActualHoursChanged which are now defined above.
    const actualHoursChanged = !isFixedFee && rawActualHoursChanged; // Retains original meaning for specific logic that needs it combined

    // const statusChanged = editableStatus !== originalStatus; // MOVED UP
    // const isNoPhotoJobChanged = isNoPhotoJob !== originalIsNoPhotoJob; // MOVED UP
    // const notesForAdminChanged = notesForAdmin !== originalNotesForAdmin; // MOVED UP

    // const jobDateChanged = editableJobDate !== originalJobDate; // This was the one that had a duplicate, ensure it's declared once. // REMOVED DUPLICATE

    // const isDirty = statusChanged || ... // MOVED UP (this definition block)

    // The monthly cap is worked out from the jobDate already stored, so a date edit that moves the
    // job into another month has to land before the check runs or it counts against the wrong month.
    if (jobDateChanged && job?.allocationType === 'monthly' && editableJobDate.slice(0, 7) !== originalJobDate.slice(0, 7)) {
      setSaving(true);
      try {
        await updateJobDetails(jobId, { jobDate: editableJobDate });
        setOriginalJobDate(editableJobDate);
        setJob(prevJob => ({ ...prevJob, jobDate: editableJobDate }));
      } catch (err) {
        setSaveError(`Failed to save the scheduled date: ${err.message}`);
        return;
      } finally {
        setSaving(false);
      }
    }

    // --- MONTHLY ALLOCATION VALIDATION ---
    if (!bypassHourModalCheck && rawActualHoursChanged && !isFixedFee && job?.allocationType === 'monthly') {
      try {
        const proposedHours = parseFloat(actualHours);
        if (!isNaN(proposedHours) && proposedHours > 0) {
          console.log('[handleSave] Validating monthly allocation for proposed hours:', proposedHours);
          const validationResult = await validateMonthlyHours(job.id, proposedHours);

          if (validationResult.status === 'success' && validationResult.isMonthlyAllocation) {
            if (!validationResult.isValid) {
              setSaveError(validationResult.breachMessage
                ? `${validationResult.breachMessage} Please contact the office if these hours are correct.`
                : `Proposed hours (${proposedHours}) would exceed monthly allocation. Remaining: ${validationResult.remainingHours} hours.`);
              return;
            }

            // If this would complete the allocation AND there are multiple jobs in the month, show confirmation modal
            // If only 1 job in month, proceed with normal save (invoice auto-generated by backend)
            if (validationResult.wouldCompleteAllocation && validationResult.totalJobsInMonth > 1) {
              setMonthlyAllocationModalInfo({
                type: 'complete',
                proposedHours,
                totalAfterProposed: validationResult.totalAfterProposed,
                monthlyAllocationTotal: validationResult.monthlyAllocationTotal,
                monthYear: validationResult.monthYear,
                scheduleId: validationResult.scheduleId,
                remainingJobsInMonth: validationResult.remainingJobsInMonth
              });
              setShowMonthlyAllocationModal(true);
              return; // Wait for user confirmation
            }
          }
        }
      } catch (error) {
        console.error('[handleSave] Monthly allocation validation error:', error);
        setSaveError(`Error validating monthly allocation: ${error.message}`);
        return;
      }
    }
    // --- END MONTHLY ALLOCATION VALIDATION ---

    // --- PRE-SAVE VALIDATION FOR ACTUAL HOURS ---
    // if (actualHoursChanged && !isFixedFee) { // Old condition
    if (rawActualHoursChanged && !isFixedFee) { // Use rawActualHoursChanged here
      const currentActualHoursVal = parseFloat(actualHours);
      const allottedHoursVal = job?.allotedHours;
      if (allottedHoursVal != null && !isNaN(currentActualHoursVal) && currentActualHoursVal > allottedHoursVal) {
        setSaveError(`Actual Hours (${currentActualHoursVal}) cannot exceed Allocated Hours (${allottedHoursVal} hrs).`);
        return; // Reject save
      }
    }
    // --- END PRE-SAVE VALIDATION ---

    // --- NEW HOUR UPDATE MODAL LOGIC (only if status is NOT being changed) ---
    // if (!bypassHourModalCheck && actualHoursChanged && !statusChanged && !isFixedFee && [\\\'approved\\\', \\\'service-delayed\\\\\\'].includes(originalStatus)) { // Old condition
    if (!bypassHourModalCheck && rawActualHoursChanged && !statusChanged && !isFixedFee && ['approved', 'service-delayed'].includes(originalStatus) && job?.allocationType !== 'monthly') { // Use rawActualHoursChanged here
      console.log('[handleSave] Conditions met for showing hour update modal. bypassHourModalCheck:', bypassHourModalCheck, 'rawActualHoursChanged:', rawActualHoursChanged, 'statusChanged:', statusChanged, 'isFixedFee:', isFixedFee, 'originalStatus:', originalStatus);
      const currentActualHoursVal = parseFloat(actualHours); // actualHours is a string from input
      const allottedHoursVal = job?.allotedHours;

      // If actualHours is being set to "0" (explicitly), do not show the hour update modal.
      // Allow saving 0 hours directly.
      if (actualHours.trim() === "0") {
        console.log('[handleSave] Actual hours input is "0", skipping hour update modal.');
        // Fall through to proceed with saving 0 hours without modal.
      } else if (allottedHoursVal != null && !isNaN(currentActualHoursVal) && currentActualHoursVal > 0) {
        // Only show modal if actual hours are positive and comparable to allotted hours.
        if (currentActualHoursVal < allottedHoursVal) {
          setHourUpdateModalInfo({ type: 'lessThan', hours: currentActualHoursVal, allotted: allottedHoursVal });
          setShowHourUpdateModal(true);
          console.log('[handleSave] Setting showHourUpdateModal to true (lessThan)');
          return; // Return to show modal
        }
        if (currentActualHoursVal === allottedHoursVal) {
          setHourUpdateModalInfo({ type: 'equalTo', hours: currentActualHoursVal, allotted: allottedHoursVal });
          setShowHourUpdateModal(true);
          console.log('[handleSave] Setting showHourUpdateModal to true (equalTo)');
          return; // Return to show modal
        }
      }
      // If actualHours is empty, NaN, or > allottedHours (and not 0), it will fall through here
      // and proceed to the main save logic, which has its own validations.
    }
    // --- END NEW HOUR UPDATE MODAL LOGIC ---

    if (!isDirty || saving) {
      // If no actual changes related to core fields, but notesForAdmin might have changed
      // If isDirty is false (meaning no core changes) but notesForAdminChanged is true, we proceed.
      // If isDirty is true, we proceed.
      // If saving is true, we return.
      if (saving) return;
      if (!isDirty && !notesForAdminChanged) { // If truly nothing changed
        setSuccessMessage("No changes to save.");
        return;
      }
    }

    let showCompletionSuggestion = false; // Reinstated
    // if (isPerVisit && originalStatus === 'approved' && actualHoursChanged) { // Reinstated block // Old condition
    if (isPerVisit && originalStatus === 'approved' && rawActualHoursChanged && !isFixedFee) { // Use rawActualHoursChanged and ensure !isFixedFee for per-visit logic
      const hours = actualHours.trim() === '' ? null : parseFloat(actualHours);
      const allotted = job?.allotedHours;

      if (hours !== null && allotted != null && hours > allotted) {
        // This error is now handled by the new modal logic earlier or the direct save validation
        // setSaveError(`Actual Hours (${hours}) cannot exceed Allocation per visit (${allotted} hrs).`);
        // return;
      }
      if (hours !== null && allotted != null && hours === allotted && editableStatus === 'approved') {
        showCompletionSuggestion = true; // Reinstated
      }
    } // Reinstated block

    if (statusChanged && editableStatus === 'completed') {
      const hoursValue = isFixedFee ? job?.allotedHours : parseFloat(actualHours);
      const hasValidHoursForCompletion = isFixedFee || (hoursValue != null && hoursValue > 0);
      const photosRequirementMet = isNoPhotoJob || (job?.photoUrls?.filter(url => !deletedPhotoUrls.includes(url)).length || 0) > 0;


      if (!hasValidHoursForCompletion || !photosRequirementMet) {
        let errorMsg = 'Cannot complete job: ';
        const errors = [];
        if (!hasValidHoursForCompletion) {
          errors.push('Actual Hours must be entered and greater than 0');
        }
        if (!photosRequirementMet) {
          errors.push('at least one photo must be uploaded (or "No photos required" checked)');
        }
        errorMsg += errors.join(' and ') + '.';
        setSaveError(errorMsg);
        return;
      }

      if (!editableCompletionDate) {
        setSaveError("Completed on date cannot be empty.");
        return;
      }
      if (editableCompletionDate > getLocalDateString()) {
        setSaveError("Completed on date cannot be in the future.");
        return;
      }

      // Carry over the date the franchisee entered in the "Completed on" field
      const proposedDate = editableCompletionDate;
      setCompletionDate(proposedDate);

      // Show the completion date modal first - duplicate check will happen in confirmAndCompleteJob
      // after user selects/confirms their date
      setShowCompletionModal(true);
      return;
    }

    if (editableStatus === 'service-delayed' && !notesForAdmin.trim()) {
      setSaveError("Notes for Admin are required when status is \'Service Delayed\'.");
      return;
    }

    const dataToUpdate = {};
    let changesMade = false; // This will be true if any of the core fields or notesForAdmin change

    // Logic for populating dataToUpdate, ensuring original structure is respected
    // and notesForAdmin is included.

    if (isFixedFee) {
      // For fixed fee, actualHours are typically not franchisee-editable but set to allottedHours if status changes.
      // We only care about status or isNoPhotoJob changes for fixed fee in this direct save context.
      // Actual hours for fixed fee are handled more implicitly or upon completion.
      if (statusChanged || isNoPhotoJobChanged) {
         // If status changes (e.g. to completed), actualHours might be set to allottedHours by backend or completion logic.
         // For now, we don't explicitly set actualHours here for fixed fee unless it's part of a specific flow like completion.
        changesMade = true; // A change occurred
      }
    } else if (actualHoursChanged) { // For non-fixed fee jobs
      const trimmedActualHours = actualHours.trim();

      if (trimmedActualHours === '') {
        setSaveError("Actual Hours cannot be empty. Please enter 0 if no hours were worked.");
        return;
      }

      const hours = parseFloat(trimmedActualHours);

      if (isNaN(hours)) {
        setSaveError("Invalid format for Actual Hours. Please enter a valid number.");
        return;
      }

      if (hours < 0) {
        setSaveError("Actual Hours cannot be negative.");
        return;
      }
      // The per-visit check for hours > allotted was done earlier.
      dataToUpdate.actualHours = hours;
      changesMade = true;
    }

    if (statusChanged) {
      dataToUpdate.status = editableStatus;
      changesMade = true;
      // If status changes away from 'service-delayed', clear reasonForDelay (no longer needed)
      // if (editableStatus !== 'service-delayed' && originalStatus === 'service-delayed' && reasonForDelay) { // Removed
      //   dataToUpdate.reasonForDelay = null; // Explicitly clear it // Removed
      // } // Removed
    }

    // if (editableStatus === 'service-delayed' && reasonForDelayChanged) { // Removed
    //   dataToUpdate.reasonForDelay = reasonForDelay.trim(); // Removed
    //   changesMade = true; // Removed
    // } else if (statusChanged && editableStatus === 'service-delayed' && !dataToUpdate.reasonForDelay && !reasonForDelay.trim()) { // Removed
      // This case is already handled by the check: editableStatus === 'service-delayed' && !reasonForDelay.trim() // Removed
      // setSaveError("Reason for delay is required when status is 'Service Delayed'."); // Removed
      // return; // Removed
    // } // Removed


    if (isNoPhotoJobChanged) {
      dataToUpdate.isNoPhotoJob = isNoPhotoJob;
      changesMade = true;
    }

    if (jobDateChanged && (editableStatus === 'approved' || editableStatus === 'completed')) { // Allow jobDate change if status is 'approved' or 'completed'
      dataToUpdate.jobDate = editableJobDate;
      changesMade = true;
    }

    // Add notesForAdmin to dataToUpdate if changed
    if (notesForAdminChanged) {
      dataToUpdate.notesForAdmin = notesForAdmin.trim();
      changesMade = true;
    }

    // Add reasonForDelay when status is service-delayed (backend requires this field)
    if (editableStatus === 'service-delayed' && notesForAdmin.trim()) {
      dataToUpdate.reasonForDelay = notesForAdmin.trim();
      changesMade = true;
    }
    
    // Consolidate photo URL updates (if any from deletions)
    // This was missing in the original handleSave but present in my previous version and is good to have.
    if (job && job.photoUrls && deletedPhotoUrls.length > 0) {
        const currentPhotos = job.photoUrls.filter(url => !deletedPhotoUrls.includes(url));
        if (currentPhotos.length !== job.photoUrls.length) {
            dataToUpdate.photoUrls = currentPhotos;
            changesMade = true;
        }
    }


    if (!changesMade) {
      setSuccessMessage("No changes to save."); // Or simply return
      return;
    }

    setSaving(true);
    try {
      await updateJobDetails(jobId, dataToUpdate);
      setSuccessMessage("Job details updated successfully!");
      setDeletedPhotoUrls([]); // Clear successfully deleted photo URLs

      if (showCompletionSuggestion) { // Reinstated block
        setCompletionSuggestion("Actual hours now match the allocation per visit. You can mark this job as completed if finished.");
      } // Reinstated block

      // Update original values to reflect saved changes
      if (dataToUpdate.hasOwnProperty('status')) {
        setOriginalStatus(dataToUpdate.status);
        setJob(prevJob => ({ ...prevJob, status: dataToUpdate.status }));
         // If status changed to 'approved' from 'service-delayed', clear reasonForDelay related states (no longer needed)
        // if (originalStatus === 'service-delayed' && dataToUpdate.status === 'approved') {
        //     setReasonForDelay(''); 
        //     setOriginalReasonForDelay(''); 
        // }
      }
      if (dataToUpdate.hasOwnProperty('actualHours')) {
        setOriginalActualHours(String(dataToUpdate.actualHours ?? ''));
        setJob(prevJob => ({ ...prevJob, actualHours: dataToUpdate.actualHours }));
      }
      if (dataToUpdate.hasOwnProperty('isNoPhotoJob')) {
        setOriginalIsNoPhotoJob(dataToUpdate.isNoPhotoJob);
        setJob(prevJob => ({ ...prevJob, isNoPhotoJob: dataToUpdate.isNoPhotoJob }));
      }
      // if (dataToUpdate.hasOwnProperty('reasonForDelay')) { // Removed
      //   setOriginalReasonForDelay(dataToUpdate.reasonForDelay || '');
      //   setJob(prevJob => ({ ...prevJob, reasonForDelay: dataToUpdate.reasonForDelay }));
      // }
      if (dataToUpdate.hasOwnProperty('jobDate')) {
        setOriginalJobDate(dataToUpdate.jobDate);
        setJob(prevJob => ({ ...prevJob, jobDate: dataToUpdate.jobDate }));
      }
      if (dataToUpdate.hasOwnProperty('notesForAdmin')) {
        setOriginalNotesForAdmin(dataToUpdate.notesForAdmin); // Use the value that was sent
        setJob(prevJob => ({ ...prevJob, notesForAdmin: dataToUpdate.notesForAdmin }));
      }
      if (dataToUpdate.hasOwnProperty('photoUrls')) {
        setJob(prevJob => ({ ...prevJob, photoUrls: dataToUpdate.photoUrls }));
      }


    } catch (err) {
      setSaveError(`Failed to save changes: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // --- MODAL HANDLERS FOR HOUR UPDATE ---
  const proceedWithHourUpdate = () => {
    console.log('[proceedWithHourUpdate] Called');
    setShowHourUpdateModal(false);
    setHourUpdateModalInfo(null);
    handleSave(true); // Call handleSave, bypassing the modal check this time
  };

  const completeJobFromHourModal = () => {
    console.log('[completeJobFromHourModal] Called');
    setShowHourUpdateModal(false);
    setHourUpdateModalInfo(null);
    setEditableStatus('completed');
    // confirmAndCompleteJob(); // This function handles setting status and other completion logic
    // Directly call handleSave with bypass and ensure status is 'completed'
    // The actual completion logic (setting date, etc.) will be handled within handleSave or confirmAndCompleteJob
    // For now, let's ensure handleSave is called correctly after status update.
    // We need to ensure that the 'completed' status is part of the save operation.
    // The original confirmAndCompleteJob might be better if it orchestrates the full completion.
    // Let's stick to the original call for now if it's more robust.
    confirmAndCompleteJob(); 
  };

  const cancelHourUpdateModal = () => {
    console.log('[cancelHourUpdateModal] Called');
    setShowHourUpdateModal(false);
    setHourUpdateModalInfo(null);
  };

  // --- MONTHLY ALLOCATION MODAL HANDLERS ---
  const proceedWithMonthlyAllocation = () => {
    console.log('[proceedWithMonthlyAllocation] Called');
    setShowMonthlyAllocationModal(false);
    setMonthlyAllocationModalInfo(null);
    handleSave(true); // Call handleSave, bypassing the modal check this time
  };

  const completeAllocationAndJobs = async () => {
    console.log('[completeAllocationAndJobs] Called');
    setShowMonthlyAllocationModal(false);
    const modalInfo = monthlyAllocationModalInfo;
    setMonthlyAllocationModalInfo(null);
    setSaving(true);
    setSaveError('');

    try {
      console.log('[completeAllocationAndJobs] Completing current job with proposed hours...');

      // Complete the current job directly with the proposed hours
      const proposedHours = modalInfo.proposedHours;
      const dataToUpdate = {
        status: 'completed',
        actualHours: proposedHours,
        completionDate: editableCompletionDate || getLocalDateString(),
        isNoPhotoJob: isNoPhotoJob
      };

      // This route skips the completion modal, so an edited scheduled date would otherwise be lost.
      if (editableJobDate && editableJobDate !== originalJobDate) {
        dataToUpdate.jobDate = editableJobDate;
      }

      console.log('[completeAllocationAndJobs] Updating current job with data:', dataToUpdate);

      // Update the current job
      await updateJobDetails(jobId, dataToUpdate);

      // Update local state
      setOriginalStatus('completed');
      setEditableStatus('completed');
      setOriginalActualHours(String(proposedHours));
      const savedCompletionDate = formatDateToInput(dataToUpdate.completionDate);
      setEditableCompletionDate(savedCompletionDate);
      setOriginalCompletionDate(savedCompletionDate);
      if (dataToUpdate.hasOwnProperty('jobDate')) {
        setOriginalJobDate(dataToUpdate.jobDate);
      }
      setJob(prevJob => ({
        ...prevJob,
        status: 'completed',
        actualHours: proposedHours,
        completionDate: dataToUpdate.completionDate,
        jobDate: dataToUpdate.hasOwnProperty('jobDate') ? dataToUpdate.jobDate : prevJob.jobDate,
        isNoPhotoJob: isNoPhotoJob
      }));

      console.log('[completeAllocationAndJobs] Current job completed, now generating invoices for completed monthly allocation jobs...');

      // Generate invoices for all completed jobs in this monthly allocation
      const { generateMonthlyAllocationInvoice } = await import('../services/jobService');
      const result = await generateMonthlyAllocationInvoice(modalInfo.scheduleId, modalInfo.monthYear);

      console.log('[completeAllocationAndJobs] Invoice generation result:', result);

      // Build success message
      let message = 'Job completed successfully! ';
      if (result.invoiceIds && result.invoiceIds.length > 0) {
        message += `Generated ${result.invoiceIds.length} invoice(s) for completed jobs in ${modalInfo.monthYear}.`;
      } else {
        message += 'No new invoices generated (may already exist).';
      }

      // Warn about remaining incomplete jobs
      if (modalInfo.remainingJobsInMonth > 1) {
        message += ` Note: ${modalInfo.remainingJobsInMonth - 1} job(s) remain incomplete in ${modalInfo.monthYear}.`;
      }

      setSuccessMessage(message);
    } catch (error) {
      console.error('[completeAllocationAndJobs] Error:', error);
      setSaveError(`Error completing allocation: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const cancelMonthlyAllocationModal = () => {
    console.log('[cancelMonthlyAllocationModal] Called');
    setShowMonthlyAllocationModal(false);
    setMonthlyAllocationModalInfo(null);
  };
  // --- END MONTHLY ALLOCATION MODAL HANDLERS ---

  // --- DUPLICATE JOB WARNING MODAL HANDLERS ---
  const cancelDuplicateWarning = () => {
    console.log('[cancelDuplicateWarning] Returning user to completion date modal');
    setShowDuplicateWarningModal(false);
    setDuplicateJobInfo(null);
    setShowCompletionModal(true);
  };
  const dismissDuplicateWarning = () => {
    console.log('[dismissDuplicateWarning] Dismissing duplicate warning modal');
    setShowCompletionModal(false);
    setShowDuplicateWarningModal(false);
    setDuplicateJobInfo(null);
  };
  const proceedDespiteDuplicateWarning = () => {
    console.log('[proceedDespiteDuplicateWarning] User chose to complete anyway');
    setShowDuplicateWarningModal(false);
    setDuplicateJobInfo(null);
    confirmAndCompleteJob({ skipDuplicateCheck: true });
  };
  // --- END DUPLICATE JOB WARNING MODAL HANDLERS ---

  // --- EARLIER INCOMPLETE JOBS MODAL HANDLERS ---
  const checkForEarlierIncompleteJobs = async () => {
    if (saving) return; // Prevent duplicate calls
    if (!job?.scheduleId || !job?.id || !job?.fseId) {
      console.log('[checkForEarlierIncompleteJobs] Missing required job data');
      return;
    }

    setSaving(true); // Disable the button immediately to prevent multi-tap
    try {
      console.log('[checkForEarlierIncompleteJobs] Checking for earlier jobs...');
      const earlierJobs = await getEarlierIncompleteJobs({
        scheduleId: job.scheduleId,
        currentJobId: job.id,
        currentJobDate: editableJobDate || getLocalDateString(),
        fseId: job.fseId,
      });

      if (earlierJobs && earlierJobs.length > 0) {
        console.log('[checkForEarlierIncompleteJobs] Found earlier jobs:', earlierJobs.length);
        setEarlierIncompleteJobs(earlierJobs);
        setShowEarlierJobsModal(true);
        setSaving(false); // Re-enable so the earlier-jobs modal can trigger completion
      } else {
        confirmAndCompleteJob()
      }
    } catch (error) {
      console.error('[checkForEarlierIncompleteJobs] Error:', error);
      setSaving(false); // Re-enable on error so user can retry
      // Don't block the completion flow if this check fails
    }
  };

  const handleCancelEarlierJobs = async () => {
    if (earlierIncompleteJobs.length === 0) {
      setShowEarlierJobsModal(false);
      return;
    }

    setCancellingEarlierJobs(true);
    try {
      const jobIds = earlierIncompleteJobs.map(j => j.id);
      const result = await cancelJobs(jobIds);

      if (result.successCount > 0) {
        setSuccessMessage(prev => {
          const base = prev || '';
          return `${base} ${result.successCount} earlier job(s) have been cancelled.`.trim();
        });
      }

      if (result.errors.length > 0) {
        console.error('[handleCancelEarlierJobs] Some jobs failed to cancel:', result.errors);
        setSaveError(`Some jobs could not be cancelled. Please try again.`);
      }
    } catch (error) {
      console.error('[handleCancelEarlierJobs] Error:', error);
      setSaveError(`Failed to cancel earlier jobs: ${error.message}`);
    } finally {
      confirmAndCompleteJob()
      setCancellingEarlierJobs(false);
      setShowEarlierJobsModal(false);
      setEarlierIncompleteJobs([]);
    }
  };

  const dismissEarlierJobsModal = () => {
    console.log('[dismissEarlierJobsModal] User chose to keep earlier jobs');
    setShowEarlierJobsModal(false);
    setEarlierIncompleteJobs([]);
    confirmAndCompleteJob()
  };

  const cancelEarlierJobsModal = () => {
    console.log('[cancelEarlierJobsModal] User cancelled completion');
    setShowEarlierJobsModal(false);
    setEarlierIncompleteJobs([]);
  };
  // --- END EARLIER INCOMPLETE JOBS MODAL HANDLERS ---

  // --- CANCEL JOB MODAL HANDLERS ---
  const closeCancelJobModal = () => {
    setShowCancelJobModal(false);
    setEditableStatus(originalStatus);
  };

  const rescheduleInsteadOfCancel = () => {
    setShowCancelJobModal(false);
    setEditableStatus(originalStatus);
    setSuccessMessage('To reschedule, change the scheduled date above, then save.');
    dateInputRef.current?.focus();
  };

  const confirmCancelJob = () => {
    setShowCancelJobModal(false);
    handleSave(false, true);
  };
  // --- END CANCEL JOB MODAL HANDLERS ---

  // --- END MODAL HANDLERS ---

  const confirmAndCompleteJob = async ({ skipDuplicateCheck = false } = {}) => {

    setShowCompletionModal(false);
    setSaving(true);
    setSaveError('');

    if (completionDate) {
      const selectedDate = new Date(completionDate);
      const dayOfWeek = selectedDate.getDay();
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        setSaveError('Completion date cannot fall on a weekend.');
        setSaving(false);
        return;
      }
    }

    const isFixedFee = job?.allocationType === 'fixed-fee';
    // Use current actualHours from state if not fixed fee, otherwise allottedHours
    // If actualHours was changed and triggered this flow, it's in `actualHours` state.
    // If it wasn't changed but status was set to complete, use `originalActualHours`.
    let hoursToSave;
    if (isFixedFee) {
          hoursToSave = null;
    } else {
        const currentActual = actualHours.trim() === '' ? null : parseFloat(actualHours);
        if (currentActual !== null && !isNaN(currentActual) && currentActual > 0) { // Ensure it's positive for completion
            hoursToSave = currentActual;
        } else {
            // Fallback to original if current is invalid/empty or not positive
            const originalActualNum = originalActualHours.trim() === '' ? null : parseFloat(originalActualHours);
            if (originalActualNum !== null && !isNaN(originalActualNum) && originalActualNum > 0) {
                hoursToSave = originalActualNum;
            } else {
                 hoursToSave = null; // Explicitly null if no valid hours found
            }
        }
    }


    if (!isFixedFee && (hoursToSave === null || hoursToSave <= 0)) {
      setSaveError('Cannot complete job: Actual Hours must be greater than 0.');
      setSaving(false);
      return;
    }

    const photosRequirementMet = isNoPhotoJob || (job?.photoUrls?.filter(url => !deletedPhotoUrls.includes(url)).length || 0) > 0;
    if (!photosRequirementMet) {
        setSaveError('Cannot complete job: at least one photo must be uploaded (or "No photos required" checked).');
        setSaving(false);
        return;
    }

    // Check for nearby completed/invoiced jobs using the user's selected completion date
    if (!skipDuplicateCheck) {
      try {
        console.log('[confirmAndCompleteJob] Checking for nearby jobs with completionDate:', completionDate);
        const nearbyJobs = await checkNearbyCompletedJobs({
          jobId: job.id,
          fseId: job.fseId,
          householdId: job.householdId,
          services: job.services,
          proposedCompletionDate: completionDate,
          frequencyType: job.frequencyType,
          frequencyValue: job.frequencyValue,
        });

        console.log('[confirmAndCompleteJob] nearbyJobs:', nearbyJobs);

        if (nearbyJobs && nearbyJobs.length > 0) {
          const nearbyJob = nearbyJobs[0];
          const matchingServices = (nearbyJob.services || [])
            .filter(s => (job.services || []).some(js => js.serviceTypeId && js.serviceTypeId === s.serviceTypeId))
            .map(s => s.name)
            .join(', ');

          const address = job?.householdInfo?.addressLine1 || 'this property';
          const householdName = `${job?.householdInfo?.firstName || ''} ${job?.householdInfo?.lastName || ''}`.trim();

          setDuplicateJobInfo({
            nearbyJob,
            serviceType: matchingServices || 'this service',
            completionDate: formatTimestamp(nearbyJob.completionDate),
            clientName: householdName || 'this client',
            address
          });
          setShowDuplicateWarningModal(true);
          setSaving(false);
          return;
        }
      } catch (error) {
        console.error('[confirmAndCompleteJob] Error checking for nearby jobs:', error);
        // Continue with completion if check fails
      }
    }

    const dataToUpdate = {
      status: 'completed',
      actualHours: hoursToSave,
      isNoPhotoJob: isNoPhotoJob, // Current state of the checkbox
      completionDate: completionDate, // From modal
    };

    // handleSave opens the completion modal and returns before its own jobDate write, so an edited
    // scheduled date can only be persisted here.
    if (editableJobDate && editableJobDate !== originalJobDate) {
      dataToUpdate.jobDate = editableJobDate;
    }

    // if (job?.reasonForDelay) { // Clear reasonForDelay when completing // Removed
    //   dataToUpdate.reasonForDelay = null; // Removed
    // } // Removed

    // Add notesForAdmin if it has changed from original or if it has content
    if (notesForAdmin.trim() !== originalNotesForAdmin.trim() || (notesForAdmin.trim() && !originalNotesForAdmin.trim())) {
      dataToUpdate.notesForAdmin = notesForAdmin.trim();
    }
    
    // Add photoUrls if they have changed due to deletions
    if (job && job.photoUrls && deletedPhotoUrls.length > 0) {
        const currentPhotos = job.photoUrls.filter(url => !deletedPhotoUrls.includes(url));
        if (JSON.stringify(currentPhotos) !== JSON.stringify(job.photoUrls)) {
             dataToUpdate.photoUrls = currentPhotos;
        }
    }


    try {
      const result = await updateJobDetails(jobId, dataToUpdate);
      let successMsg = "Job marked as completed successfully!";
      if (result?.xeroReconnectRequired) {
        successMsg += " Note: The invoice and RCTI were not sent to Xero due to a connection issue. Please go to the Invoices screen, connect to Xero, then retry sending the invoice and RCTI to Xero.";
      }
      setSuccessMessage(successMsg);
      setBillingJustFailed(!!result?.billingGenerationFailed);
      setDeletedPhotoUrls([]);

      setOriginalStatus('completed');
      setEditableStatus('completed');
      setOriginalActualHours(String(hoursToSave));
      setOriginalIsNoPhotoJob(isNoPhotoJob);
      const savedCompletionDate = formatDateToInput(completionDate);
      setEditableCompletionDate(savedCompletionDate);
      setOriginalCompletionDate(savedCompletionDate);
      // setOriginalReasonForDelay(''); // Removed
      // setReasonForDelay('');       // Removed

      if (dataToUpdate.hasOwnProperty('notesForAdmin')) {
        setOriginalNotesForAdmin(dataToUpdate.notesForAdmin);
      }
      if (dataToUpdate.hasOwnProperty('jobDate')) {
        setOriginalJobDate(dataToUpdate.jobDate);
      }

      setJob(prevJob => ({
        ...prevJob,
        status: 'completed',
        actualHours: hoursToSave,
        isNoPhotoJob: isNoPhotoJob,
        completionDate: completionDate,
        // reasonForDelay: null, // Removed
        jobDate: dataToUpdate.hasOwnProperty('jobDate') ? dataToUpdate.jobDate : prevJob.jobDate,
        notesForAdmin: dataToUpdate.hasOwnProperty('notesForAdmin') ? dataToUpdate.notesForAdmin : prevJob.notesForAdmin,
        photoUrls: dataToUpdate.hasOwnProperty('photoUrls') ? dataToUpdate.photoUrls : prevJob.photoUrls,
      }));

      // Check for earlier incomplete jobs from the same schedule after successful completion

    } catch (err) {
      setSaveError(`Failed to mark job as completed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Shown right after completion and on every later visit, so a franchisee whose payment
  // is stuck can see it. Deliberately generic; the technical cause is admin-only.
  const billingWarning = useMemo(() => {
    if (!billingJustFailed && job?.invoiceGenerationStatus !== 'failed') {
      return '';
    }
    return "We couldn't generate the paperwork for this job, so your payment may be delayed. Please contact support.";
  }, [billingJustFailed, job]);

  const editableStatusOptions = useMemo(() => {
    console.log("Config Statuses:", configStatuses);
    console.log("Original Status:", originalStatus);

    let allowedCodes = [];
    // Define allowed transitions based on the *original* status
    if (originalStatus === 'approved') {
      allowedCodes = ['approved', 'completed', 'service-delayed', 'cancelled'];
    } else if (originalStatus === 'completed') {
      // If already completed, can only switch back to approved or stay completed
      // MODIFICATION: If completed, status cannot be changed.
      allowedCodes = ['completed'];
    } else if (originalStatus === 'service-delayed') {
      // If delayed, can move to approved, completed, cancelled, or stay delayed
      allowedCodes = ['approved', 'completed', 'service-delayed', 'cancelled'];
    }
    // For other statuses (pending-quote, pending-approval), allowedCodes remains empty,
    // meaning no options will be generated by the filter below.

    // Filter the config statuses based on the strictly allowed codes
    const options = configStatuses
      .filter(s => allowedCodes.includes(s.code))
      .map(s => ({ value: s.code, label: s.display }));

    // If originalStatus is 'completed' and no other options are allowed,
    // ensure 'completed' itself is an option so the dropdown isn't empty.
    if (originalStatus === 'completed' && options.length === 0) {
        const completedStatus = configStatuses.find(s => s.code === 'completed');
        if (completedStatus) {
            options.push({ value: completedStatus.code, label: completedStatus.display });
        }
    }


    console.log("Generated Editable Status Options:", options);
    return options;
  }, [configStatuses, originalStatus]);

  let jobDateForOverdueCheck = null;
  if (editableStatus === 'approved' && editableJobDate) {
    try {
      const selectedDate = new Date(editableJobDate);
      jobDateForOverdueCheck = new Date(selectedDate.getUTCFullYear(), selectedDate.getUTCMonth(), selectedDate.getUTCDate());
    } catch {
      if (job?.jobDate) {
        if (typeof job.jobDate.toDate === 'function') {
          jobDateForOverdueCheck = job.jobDate.toDate();
        } else if (typeof job.jobDate._seconds === 'number') {
          jobDateForOverdueCheck = new Date(job.jobDate._seconds * 1000);
        } else if (typeof job.jobDate === 'string') {
          try {
            const parsedDate = new Date(job.jobDate);
            if (!isNaN(parsedDate)) {
              jobDateForOverdueCheck = new Date(parsedDate.getUTCFullYear(), parsedDate.getUTCMonth(), parsedDate.getUTCDate());
            }
          } catch {}
        }
      }
    }
  } else if (job?.jobDate) {
    if (typeof job.jobDate.toDate === 'function') {
      jobDateForOverdueCheck = job.jobDate.toDate();
    } else if (typeof job.jobDate._seconds === 'number') {
      jobDateForOverdueCheck = new Date(job.jobDate._seconds * 1000);
    } else if (typeof job.jobDate === 'string') {
      try {
        const parsedDate = new Date(job.jobDate);
        if (!isNaN(parsedDate)) {
          jobDateForOverdueCheck = new Date(parsedDate.getUTCFullYear(), parsedDate.getUTCMonth(), parsedDate.getUTCDate());
        }
      } catch {}
    }
  }

  const todayStart = new Date(new Date().setHours(0, 0, 0, 0));
  const isOverdue = jobDateForOverdueCheck && jobDateForOverdueCheck < todayStart;

  const googleMapsUrl = getGoogleMapsUrl(job?.householdInfo);
  const hasAddress = job?.householdInfo?.addressLine1 || job?.householdInfo?.addressLine2 || job?.householdInfo?.state || job?.householdInfo?.postalCode;

  const householdContactName = `${job?.householdInfo?.firstName || ''} ${job?.householdInfo?.lastName || ''}`.trim() || 'N/A';
  const householdPhone = (job?.householdInfo?.phoneNumber || '').trim(); // human-readable display value
  const householdPhoneHref = householdPhone.replace(/[^\d+]/g, ''); // tel: link with formatting stripped; empty when no dialable digits

  const getAllocationPeriodText = (allocationType) => {
    if (!allocationType) return 'for this job'; // Default if allocationType is not available
    switch (allocationType) {
      case 'weekly':
        return 'this week';
      case 'fortnightly':
        return 'this fortnight';
      case 'monthly':
        return 'this month';
      default:
        return 'for this job'; // Fallback for 'per-visit', 'fixed-fee', etc.
    }
  };

  if (loading || loadingConfig) {
    return <div className="container mx-auto px-4 py-16 text-center">Loading job details...</div>;
  }

  if (error || configError) {
    const isPermissionError = error?.toLowerCase().includes('permission') || error?.toLowerCase().includes('forbidden');
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        {isPermissionError ? (
          <>
            <div className="text-5xl mb-4">🔒</div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              This job does not belong to your account. If you believe this is a mistake, please contact your admin.
            </p>
          </>
        ) : (
          <p className="text-red-600 mb-4">{error || configError}</p>
        )}
        <Link to="/" className="text-blue-600 hover:underline">Back to Home</Link>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="container mx-auto px-4 py-16 text-center text-gray-500">
        Job not found.
        <div className="mt-4">
          <Link to="/" className="text-blue-600 hover:underline">Back to Home</Link>
        </div>
      </div>
    );
  }

  const isFixedFee = job?.allocationType === 'fixed-fee';
  const isStandardEditable = originalStatus === 'approved';
  // MODIFICATION: Add a check if the job is completed to disable edits.
  const isJobCompleted = originalStatus === 'completed';
  // A job past completion no longer has a meaningful scheduled date or time.
  const isJobFinished = isJobCompleted || originalStatus === 'invoiced';

  // Define rawActualHoursChanged in the component scope
  const rawActualHoursChanged = actualHours !== originalActualHours;

  const isActualHoursDisabled = isJobCompleted || isFixedFee || saving || uploading || !!deletingImage || !['approved', 'service-delayed'].includes(originalStatus);

  const actualHoursChangedCheck = !isFixedFee && rawActualHoursChanged; // Use rawActualHoursChanged here
  const statusChanged = editableStatus !== originalStatus;
  const isNoPhotoJobChanged = isNoPhotoJob !== originalIsNoPhotoJob;
  const priceQuoteChangedCheck = priceQuotePerHour !== originalPriceQuotePerHour;
  // const reasonForDelayChangedCheck = reasonForDelay !== originalReasonForDelay;
  const jobDateChangedCheck = editableJobDate !== originalJobDate;
  const completionDateChangedCheck = editableCompletionDate !== originalCompletionDate;
  // "Completed on" replaces "Scheduled for" the moment Completed is picked, not once it is saved.
  const showsCompletionDate = editableStatus === 'completed' || originalStatus === 'invoiced';
  const notesForAdminChangedCheck = notesForAdmin !== originalNotesForAdmin; 

  let isDirty = false;
  if (originalStatus === 'pending-quote') {
    isDirty = priceQuoteChangedCheck;
  } else if (['approved', 'completed', 'service-delayed'].includes(originalStatus)) {
    const dateChangeIsRelevant = editableStatus === 'approved' && jobDateChangedCheck;
    // isDirty = statusChanged || isNoPhotoJobChanged || actualHoursChangedCheck ||
    //           (editableStatus === 'service-delayed' && reasonForDelayChangedCheck) ||
    //           dateChangeIsRelevant || notesForAdminChangedCheck; // Modified
    isDirty = statusChanged || isNoPhotoJobChanged || actualHoursChangedCheck || dateChangeIsRelevant || notesForAdminChangedCheck;
  }
  // A completed job locks every other field, so its completion date is the only thing left to save.
  if (isJobCompleted) {
    isDirty = completionDateChangedCheck;
  }

  const totalCurrentPhotos = job?.photoUrls?.length || 0;
  const canUploadMore = !isJobCompleted && !isNoPhotoJob && totalCurrentPhotos < 5 && originalStatus !== 'pending-quote';

  const saveButtonLabel = originalStatus === 'pending-quote' ? 'Submit Quote' : 'Save Changes';
  const canSaveChanges = ['approved', 'completed', 'service-delayed'].includes(originalStatus);
  const canSubmitQuote = originalStatus === 'pending-quote';
  const showSaveButton = (canSaveChanges || canSubmitQuote) && !isReadOnly;

  return (
    <div className="container mx-auto px-4 py-10 max-w-3xl">
        <div className="mb-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-sm text-gray-500 hover:text-gray-900 font-medium transition-colors"
          >
            <FaArrowLeft className="mr-2" /> Back to Job List
          </button>
        </div>

        <div className="mb-6 p-4 bg-gray-50 rounded border border-gray-200">
          <div className="flex justify-between items-start mb-3">
            <div>
              <h3 className="text-xl font-semibold text-gray-700">
                Job for {(`${job?.householdInfo?.firstName || ''} ${job?.householdInfo?.lastName || ''}`.trim() || 'Client')}
              </h3>
              {(job?.householdInfo?.addressLine1 || job?.householdInfo?.state || job?.householdInfo?.postalCode) && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {[job.householdInfo.addressLine1, job.householdInfo.addressLine2, job.householdInfo.state, job.householdInfo.postalCode].filter(Boolean).join(', ')}
                </p>
              )}
            </div>
            {showSaveButton && (
              <button
                onClick={() => handleSave()}
                disabled={!isDirty || !!saveError || saving || uploading || !!deletingImage}
                className="inline-flex items-center px-3 py-1 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : saveButtonLabel}
              </button>
            )}
          </div>
          {successMessage && (
            <p className="mb-3 text-sm text-green-600 bg-green-100 p-2 rounded border border-green-300">
              {successMessage}
            </p>
          )}
          {billingWarning && (
            <p className="mb-3 text-sm text-amber-700 bg-amber-100 p-2 rounded border border-amber-300">
              {billingWarning}
            </p>
          )}
          {completionSuggestion && ( // Reinstated
            <p className="mb-3 text-sm text-blue-600 bg-blue-100 p-2 rounded border border-blue-300">
              {completionSuggestion}
            </p>
          )} 
          {(saveError || deleteError) && <p className="mb-3 text-sm text-red-600">{saveError || deleteError}</p>}

          {/* ── Row 1: Status + Scheduled Date ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
              {editableStatusOptions && editableStatusOptions.length > 0 ? (
                <select
                  value={editableStatus}
                  onChange={handleStatusChange}
                  disabled={isReadOnly || saving || uploading || !!deletingImage || loadingConfig || isJobCompleted}
                  className={`block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md shadow-sm ${saveError && (saveError.includes('Status') || saveError.includes('Notes for Admin')) ? 'border-red-500' : ''} disabled:bg-gray-100`}
                >
                  {editableStatusOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              ) : (
                <div className="py-2"><StatusBadge statusCode={job?.status} /></div>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                {showsCompletionDate ? 'Completed on' : (
                  <>Scheduled for {isOverdue && editableStatus === 'approved' && <span className="text-red-500 ml-1">(Overdue)</span>}</>
                )}
              </label>
              {isReadOnly || originalStatus === 'invoiced' ? (
                <div className={`py-2 text-sm ${isOverdue && editableStatus === 'approved' ? 'text-red-600 font-medium' : 'text-gray-800'}`}>
                  {formatTimestamp(showsCompletionDate ? job?.completionDate : job?.jobDate)}
                </div>
              ) : (
                <input
                  type="date"
                  ref={dateInputRef}
                  value={showsCompletionDate ? editableCompletionDate : editableJobDate}
                  max={showsCompletionDate ? getLocalDateString() : undefined}
                  onChange={showsCompletionDate ? handleCompletionDateChange : handleJobDateChange}
                  disabled={saving || uploading || !!deletingImage}
                  className={`block w-full px-3 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md shadow-sm ${saveError && (saveError.includes('Job Date') || saveError.includes('Completed on') || saveError.includes('past')) ? 'border-red-500' : ''} disabled:bg-gray-100 disabled:cursor-not-allowed`}
                />
              )}
            </div>
          </div>

          {/* ── Row 2: Actual Hours + Allotted Hours (non-fixed-fee, non-pending-quote) ── */}
          {!isFixedFee && originalStatus !== 'pending-quote' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Actual Hours</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={actualHours}
                  onChange={handleActualHoursChange}
                  disabled={isReadOnly || isActualHoursDisabled || isJobCompleted}
                  className={`block w-full px-3 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md shadow-sm ${saveError && (saveError.includes('Actual Hours') || saveError.includes('greater than 0')) ? 'border-red-500' : ''} disabled:bg-gray-100 disabled:cursor-not-allowed`}
                  placeholder="e.g., 2.5"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Allocation</label>
                <div className="py-2 text-sm text-gray-800 font-medium">
                  {formatAllocationDetails(job)}
                </div>
              </div>
            </div>
          )}

          {/* ── Pending quote: quote input ── */}
          {originalStatus === 'pending-quote' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Quote / hr (Incl. GST)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={priceQuotePerHour}
                  onChange={handlePriceQuoteChange}
                  disabled={isReadOnly || saving || uploading || !!deletingImage || isJobCompleted}
                  className={`block w-full px-3 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md shadow-sm ${saveError && saveError.includes('Price Quote') ? 'border-red-500' : ''} disabled:bg-gray-100 disabled:cursor-not-allowed`}
                  placeholder="e.g., 55.00"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Allocation</label>
                <div className="py-2 text-sm text-gray-800 font-medium">
                  {formatAllocationDetails(job)}
                </div>
              </div>
            </div>
          )}

          {/* ── Monthly allocation tracker (full-width) ── */}
          {job?.allocationType === 'monthly' && monthlyAllocationInfo && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm mb-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-0 sm:space-x-2">
                <div className="flex items-center gap-2">
                  <FaHourglassHalf className="text-gray-500 flex-shrink-0" />
                  <span className="font-medium text-gray-600">Monthly Hours ({monthlyAllocationInfo.monthYear}):</span>
                </div>
                <span className="text-gray-800 font-semibold">{monthlyAllocationInfo.totalUsedHours} / {monthlyAllocationInfo.monthlyAllocationTotal} hrs</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-0 sm:space-x-2">
                <span className="text-sm text-gray-600">Available:</span>
                <div className="flex items-center gap-2">
                  <span className={`font-semibold ${monthlyAllocationInfo.remainingHours > 0 ? 'text-green-600' : 'text-gray-600'}`}>{monthlyAllocationInfo.remainingHours} hrs</span>
                  {monthlyAllocationInfo.remainingHours === 0 && <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">Complete</span>}
                  {monthlyAllocationInfo.remainingHours > 0 && monthlyAllocationInfo.remainingHours <= (monthlyAllocationInfo.monthlyAllocationTotal * 0.2) && <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Low</span>}
                </div>
              </div>
            </div>
          )}

          {/* ── Overdue warning ── */}
          {isOverdue && editableStatus === 'approved' && (
            <div className="mb-4 p-2 rounded border border-dashed border-red-300 bg-red-50">
              <p className="text-xs text-red-600 flex items-center">
                <FaExclamationTriangle className="mr-1 flex-shrink-0" />
                Job is overdue. Reschedule or update to 'Completed' if the job is done.
              </p>
            </div>
          )}

          {/* ── Job Photos ── */}
          {(['approved', 'completed', 'service-delayed'].includes(originalStatus)) && (
            <div className="pt-4 border-t border-gray-200">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Job Photos</h4>

              {/* No-photo checkbox (editable) or disclaimer (completed) */}
              {(isStandardEditable || originalStatus === 'service-delayed') ? (
                <div className="mb-3 flex items-start">
                  <input
                    id="no-photo-checkbox"
                    type="checkbox"
                    checked={isNoPhotoJob}
                    onChange={handleNoPhotoChange}
                    disabled={isReadOnly || saving || uploading || !!deletingImage || isJobCompleted}
                    className="mt-0.5 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <label htmlFor="no-photo-checkbox" className="ml-2 text-sm text-gray-700">
                    I understand that without a photo, proof of service is limited and payment may be withheld if the job is disputed.
                  </label>
                </div>
              ) : originalStatus === 'completed' && originalIsNoPhotoJob ? (
                <p className="mb-3 text-sm text-gray-700 italic">
                  I understand that without a photo, proof of service is limited and payment may be withheld if the job is disputed.
                </p>
              ) : null}

              {/* Upload UI (only when editable and not completed) */}
              {(isStandardEditable || originalStatus === 'service-delayed') && !isJobCompleted && !isReadOnly && (
                <>
                  <input
                    type="file"
                    multiple
                    accept="image/*,application/pdf"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    disabled={isNoPhotoJob || uploading || !canUploadMore || saving || !!deletingImage || isJobCompleted}
                    className="hidden"
                  />

                  {/* Desktop: drag-and-drop zone */}
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={!isNoPhotoJob && canUploadMore && !uploading && !saving && !deletingImage ? triggerFileInput : undefined}
                    className={`hidden md:block border-2 border-dashed rounded-lg p-6 mb-3 text-center transition-colors ${
                      isDragOver ? 'border-green-400 bg-green-50' : 'border-gray-300 bg-gray-50'
                    } ${isNoPhotoJob || !canUploadMore || uploading || saving || !!deletingImage ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-green-300 hover:bg-green-50'}`}
                  >
                    <FaUpload className="mx-auto mb-2 text-gray-400 text-2xl" />
                    <p className="text-sm text-gray-600">
                      {isNoPhotoJob ? 'Upload disabled' : canUploadMore ? 'Drop files here or click to choose' : 'Maximum files reached'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Images or PDF ({isNoPhotoJob ? 0 : 5 - totalCurrentPhotos} remaining)
                    </p>
                  </div>

                  {/* Mobile: upload button only */}
                  <div className="md:hidden mb-3">
                    <button
                      type="button"
                      onClick={!isNoPhotoJob && canUploadMore && !uploading && !saving && !deletingImage ? triggerFileInput : undefined}
                      disabled={isNoPhotoJob || !canUploadMore || uploading || saving || !!deletingImage}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaUpload className="mr-2 text-gray-500" />
                      {isNoPhotoJob ? 'Upload disabled' : canUploadMore ? `Choose Photos / PDF (${5 - totalCurrentPhotos} remaining)` : 'Maximum files reached'}
                    </button>
                  </div>

                  {/* Selected files preview */}
                  {selectedFiles.length > 0 && !isNoPhotoJob && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-1">{selectedFiles.length} file(s) ready to upload:</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedFiles.map((file, i) => (
                          <div key={i} className="relative border rounded overflow-hidden" title={file.name}>
                            {file.type === 'application/pdf' ? (
                              <div className="h-16 w-16 flex flex-col items-center justify-center bg-red-50">
                                <FaFilePdf className="text-red-400 text-2xl" />
                                <span className="text-xs text-red-400 mt-0.5">PDF</span>
                              </div>
                            ) : (
                              <img src={URL.createObjectURL(file)} alt={file.name} className="h-16 w-16 object-cover" />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleUpload}
                    disabled={isNoPhotoJob || uploading || selectedFiles.length === 0 || saving || !!deletingImage || isJobCompleted}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                  >
                    <FaUpload className="mr-2" />
                    {uploading ? 'Uploading...' : 'Upload Selected'}
                  </button>

                  {uploadError && <p className="mt-2 text-sm text-red-600">Error: {uploadError}</p>}
                </>
              )}

              {/* Uploaded photos thumbnails */}
              {job?.photoUrls?.length > 0 && (
                <div className="mt-4">
                  <h5 className="text-xs font-medium text-gray-500 mb-2">Uploaded Photos</h5>
                  <div className="flex flex-wrap gap-3">
                    {job.photoUrls.map((url, index) => {
                      const isPdf = url.split('?')[0].toLowerCase().endsWith('.pdf');
                      return (
                        <div key={`existing-${index}`} className="relative group border rounded">
                          <button type="button" onClick={() => handleViewPhoto(url)} className="block">
                            {isPdf ? (
                              <div className="h-20 w-20 flex flex-col items-center justify-center bg-red-50 rounded">
                                <FaFilePdf className="text-red-500 text-3xl" />
                                <span className="text-xs text-red-500 mt-1">PDF</span>
                              </div>
                            ) : (
                              <img src={url} alt={`Job Photo ${index + 1}`} className="h-20 w-20 object-cover rounded" />
                            )}
                          </button>
                          {!isReadOnly && (isStandardEditable || originalStatus === 'service-delayed') && (
                            <button
                              onClick={() => handleDeleteImage(url)}
                              disabled={deletingImage === url || saving || uploading}
                              className="absolute top-0 right-0 p-0.5 bg-red-600 text-white rounded-bl transition-opacity duration-200 disabled:opacity-50 disabled:cursor-wait"
                              aria-label="Delete image"
                            >
                              {deletingImage === url ? (
                                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : (
                                <FaTrash className="h-3 w-3" />
                              )}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Photos section for non-approved/completed statuses (read-only thumbnails) */}
          {!(['approved', 'completed', 'service-delayed'].includes(originalStatus)) && job?.photoUrls?.length > 0 && (
            <div className="pt-4 border-t border-gray-200">
              <h5 className="text-xs font-medium text-gray-500 mb-2">Uploaded Photos</h5>
              <div className="flex flex-wrap gap-3">
                {job.photoUrls.map((url, index) => {
                  const isPdf = url.split('?')[0].toLowerCase().endsWith('.pdf');
                  return (
                    <div key={`existing-ro-${index}`} className="border rounded">
                      <button type="button" onClick={() => handleViewPhoto(url)} className="block">
                        {isPdf ? (
                          <div className="h-20 w-20 flex flex-col items-center justify-center bg-red-50 rounded">
                            <FaFilePdf className="text-red-500 text-3xl" />
                            <span className="text-xs text-red-500 mt-1">PDF</span>
                          </div>
                        ) : (
                          <img src={url} alt={`Job Photo ${index + 1}`} className="h-20 w-20 object-cover rounded" />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* ── Notes for Admin ── */}
          {(originalStatus === 'approved' || originalStatus === 'service-delayed' || editableStatus === 'service-delayed') && (
            <div className="text-sm mt-4 pt-4 border-t border-gray-200">
              <label htmlFor="notesForAdmin" className="block text-xs font-medium text-gray-500 mb-1">
                Notes for Admin {editableStatus === 'service-delayed' && <span className="text-red-500">*</span>}
              </label>
              <textarea
                id="notesForAdmin"
                rows="3"
                value={notesForAdmin}
                onChange={handleNotesForAdminChange}
                disabled={isReadOnly || saving || uploading || !!deletingImage || !(['approved', 'service-delayed'].includes(originalStatus)) || isJobCompleted}
                className={`block w-full px-3 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md shadow-sm ${saveError && saveError.includes('Notes for Admin') ? 'border-red-500' : ''} disabled:bg-gray-100 disabled:cursor-not-allowed`}
                placeholder={editableStatus === 'service-delayed' ? 'Required: Enter reason for delay or other notes...' : 'Enter any notes for the admin...'}
              />
            </div>
          )}
        </div>

        {/* ── Read-only Job Information card ── */}
        <div className="mb-6 p-4 bg-gray-50 rounded border border-gray-200 text-sm">
          <h3 className="text-base font-semibold text-gray-700 mb-3">Job Details</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">

            {isJobFinished && job?.completionDate && (
              <div>
                <p className="text-xs font-medium text-gray-500">Service Completed On</p>
                <p className="text-green-700 font-medium mt-0.5">{formatTimestamp(job.completionDate)}</p>
              </div>
            )}

            {isJobFinished && job?.rctiCreatedAt && (
              <div>
                <p className="text-xs font-medium text-gray-500">Invoiced On</p>
                <p className="text-gray-800 mt-0.5 font-medium">{formatTimestamp(job.rctiCreatedAt)}</p>
              </div>
            )}

            {job?.jobDate && !isJobFinished && (
              <div>
                <p className="text-xs font-medium text-gray-500">Scheduled for</p>
                <p className="text-gray-800 mt-0.5 font-medium">{formatTimestamp(job.jobDate)}</p>
              </div>
            )}

            {!isJobFinished && (
              <div>
                <p className="text-xs font-medium text-gray-500">Time</p>
                <p className="text-gray-800 mt-0.5">{job?.jobTime || 'N/A'}</p>
              </div>
            )}

            {job?.scheduleType && (
              <div>
                <p className="text-xs font-medium text-gray-500">Schedule</p>
                <p className="text-gray-800 mt-0.5 capitalize">
                  {job.scheduleType === 'recurring' && job.frequencyType
                    ? formatFrequency(job.frequencyType, job.frequencyValue)
                    : job.scheduleType.replace('-', ' ')}
                </p>
              </div>
            )}

            {job?.allocationType && (
              <div>
                <p className="text-xs font-medium text-gray-500">
                  {isFixedFee ? 'Fixed Fee' : 'Allocation'}
                </p>
                <p className="text-gray-800 mt-0.5 font-medium">
                  {isFixedFee
                    ? `$${(job.pricePerHour ?? 0).toFixed(2)} (inc. GST)`
                    : formatAllocationDetails(job)
                  }
                </p>
              </div>
            )}

            <div>
              <p className="text-xs font-medium text-gray-500">
                {originalStatus === 'pending-approval' ? 'Submitted Quote' : 'Rate'}
              </p>
              <p className="text-gray-800 mt-0.5">
                {originalStatus === 'pending-approval' && job?.priceQuotePerHour != null
                  ? `$${job.priceQuotePerHour.toFixed(2)} / hr (Incl. GST)`
                  : isFixedFee && job?.pricePerHour != null
                    ? `$${job.pricePerHour.toFixed(2)} (Fixed Incl. GST)`
                    : job?.pricePerHour != null
                      ? `$${job.pricePerHour.toFixed(2)} / hr (Incl. GST)`
                      : 'N/A'}
              </p>
            </div>

            {job?.services?.length > 0 && (
              <div className="col-span-2 sm:col-span-3">
                <p className="text-xs font-medium text-gray-500">Services</p>
                <p className="text-gray-800 mt-0.5">{job.services.map(s => s.name).join(', ')}</p>
              </div>
            )}

            {job?.remarks && (
              <div className="col-span-2 sm:col-span-3 pt-2 border-t border-gray-200">
                <p className="text-xs font-medium text-gray-500">Remarks</p>
                <p className="text-gray-800 whitespace-pre-wrap mt-0.5">{job.remarks}</p>
              </div>
            )}

            {job?.notesForFranchisee && (
              <div className="col-span-2 sm:col-span-3 pt-2 border-t border-gray-200">
                <p className="text-xs font-medium text-gray-500">{terminology.jobNotes.title}</p>
                <p className="text-gray-800 whitespace-pre-wrap mt-0.5">{job.notesForFranchisee}</p>
              </div>
            )}
          </div>
        </div>

        <div className="mb-6 p-4 bg-gray-50 rounded border border-gray-200">
          <h3 className="text-xl font-semibold text-gray-700 mb-3">{terminology.client.singular} Information</h3>
          <div className="space-y-2 text-sm">
            <p><span className="font-medium text-gray-600">Name:</span> <span className="text-gray-800">{(job?.clientInfo?.clientName || 'N/A')}</span></p>
          </div>
        </div>

        <div className="p-4 bg-gray-50 rounded border border-gray-200">
          <h3 className="text-xl font-semibold text-gray-700 mb-3">{terminology.household.singular} Information</h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between gap-2">
              <p><span className="font-medium text-gray-600">Contact:</span> <span className="text-gray-800">{householdContactName}</span></p>
              {householdPhoneHref && (
                <a
                  href={`tel:${householdPhoneHref}`}
                  className="inline-flex items-center gap-1.5 min-h-[44px] px-1 text-blue-600 hover:underline font-medium whitespace-nowrap rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  title="Call this client"
                >
                  <FaPhoneAlt className="flex-shrink-0 text-gray-500" aria-hidden="true" />
                  {householdPhone}
                </a>
              )}
            </div>
            <p><span className="font-medium text-gray-600">Email:</span> <span className="text-gray-800">{(job?.householdInfo?.email || 'N/A')}</span></p>
            <div className="flex items-start space-x-2">
              <FaMapMarkerAlt className="text-gray-500 mt-1 flex-shrink-0" />
              <div>
                <span className="font-medium text-gray-600">Address:</span>
                {hasAddress ? (
                  googleMapsUrl ? (
                    <a
                      href={googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline block"
                      title="Open address in Google Maps"
                    >
                      {`${job?.householdInfo?.addressLine1 || ''}`}
                      {job?.householdInfo?.addressLine2 ? <><br />{job.householdInfo.addressLine2}</> : ''}
                      {job?.householdInfo?.addressLine1 || job?.householdInfo?.addressLine2 ? <br /> : ''}
                      {`${job?.householdInfo?.state || ''} ${job?.householdInfo?.postalCode || ''}`.trim()}
                    </a>
                  ) : (
                    <span className="text-gray-800 block">
                      {`${job?.householdInfo?.addressLine1 || ''}`}
                      {job?.householdInfo?.addressLine2 ? <><br />{job.householdInfo.addressLine2}</> : ''}
                      {job?.householdInfo?.addressLine1 || job?.householdInfo?.addressLine2 ? <br /> : ''}
                      {`${job?.householdInfo?.state || ''} ${job?.householdInfo?.postalCode || ''}`.trim()}
                    </span>
                  )
                ) : (
                  <span className="text-gray-800 block">No address provided</span>
                )}
              </div>
            </div>
          </div>
        </div>

      {showCompletionModal && (
        <CompletionModal
          isFixedFee={isFixedFee}
          completionDate={completionDate}
          setCompletionDate={setCompletionDate}
          saving={saving}
          onCancel={() => setShowCompletionModal(false)}
          onConfirm={async () => await checkForEarlierIncompleteJobs()}
        />
      )}

      {/* Duplicate Job Warning Modal */}
      {showDuplicateWarningModal && duplicateJobInfo && (
        <DuplicateWarningModal
          duplicateJobInfo={duplicateJobInfo}
          onCancel={cancelDuplicateWarning}
          onDismiss={dismissDuplicateWarning}
          onProceed={proceedDespiteDuplicateWarning}
        />
      )}

      {/* New Hour Update Modal */}
      {showHourUpdateModal && hourUpdateModalInfo && job?.allocationType !== 'monthly' && (
        <HourUpdateModal
          hourUpdateModalInfo={hourUpdateModalInfo}
          allocationPeriodText={getAllocationPeriodText(job?.allocationType)}
          saving={saving}
          onCancel={cancelHourUpdateModal}
          onProceed={proceedWithHourUpdate}
          onComplete={completeJobFromHourModal}
        />
      )}

      {/* Monthly Allocation Completion Modal */}
      {showMonthlyAllocationModal && monthlyAllocationModalInfo && (
        <MonthlyAllocationModal
          monthlyAllocationModalInfo={monthlyAllocationModalInfo}
          saving={saving}
          onCancel={cancelMonthlyAllocationModal}
          onSaveHoursOnly={proceedWithMonthlyAllocation}
          onCompleteAndInvoice={completeAllocationAndJobs}
        />
      )}

      {/* Earlier Incomplete Jobs Modal */}
      {showEarlierJobsModal && earlierIncompleteJobs.length > 0 && (
        <EarlierJobsModal
          jobDate={job?.jobDate}
          earlierIncompleteJobs={earlierIncompleteJobs}
          cancellingEarlierJobs={cancellingEarlierJobs}
          onCancel={cancelEarlierJobsModal}
          onDismiss={dismissEarlierJobsModal}
          onCancelJobs={handleCancelEarlierJobs}
          hasApprovedJobs={earlierIncompleteJobs.some(j => j.status === 'approved')}
        />
      )}

      {/* Cancel Job Confirmation Modal */}
      {showCancelJobModal && (
        <CancelJobModal
          saving={saving}
          onClose={closeCancelJobModal}
          onReschedule={rescheduleInsteadOfCancel}
          onConfirm={confirmCancelJob}
        />
      )}
    </div>
  );
};

export default JobDetailsPage;
